import type { AttributeGroup, BulkRefusal } from '../../api/catalog';

/**
 * A-11 — turning the catalog workbook into one `/catalog/import` submission.
 *
 * Kept apart from the screen and from exceljs so it can be tested without either:
 * the screen hands this plain rows (header first), whatever file they came from.
 *
 * The workbook is the one `scripts/make-catalog-template.mjs` writes — sheets
 * "1 Products" and "2 Variants". Sheets 3–5 (stones, certificates, images) are not
 * read: the bulk route does not carry them, and they are entered on A-09.
 */

/** BU-02 — the marker the downloaded template carries and an upload is checked against. */
export const TEMPLATE_VERSION = 'A11-2026.1';
export const ABOUT_SHEET = 'About';

export const PRODUCT_COLUMNS = [
  'product_code', 'product_name', 'collection', 'category', 'metal_type', 'description',
  'hs_code', 'country_of_origin', 'setting', 'finish', 'clasp', 'backing', 'hallmark',
  'purities', 'colours', 'carats',
] as const;

export const VARIANT_COLUMNS = [
  'product_code', 'purity', 'metal_colour', 'centre_stone_carat', 'variant_code',
  'net_metal_weight_g', 'gross_weight_g', 'total_carat_weight', 'list_price',
] as const;

/** Columns a template may carry that this route cannot import. Named, so nobody fills them in vain. */
export const NOT_IMPORTED = ['sizes'] as const;

export type SheetKind = 'products' | 'variants';

/** One sheet as read from a file: its name, and its rows with the header first. */
export interface RawSheet {
  name: string;
  rows: string[][];
}

/** BU-04 — a failure names the sheet, the row as the person sees it, the column and the reason. */
export interface RowError {
  sheet: SheetKind;
  /** The spreadsheet row number: the header is row 1. */
  row: number;
  column: string;
  reason: string;
}

export interface ImportProduct {
  name: string;
  codePrefix: string;
  metalTypeCode: string;
  collection: string | null;
  category: string | null;
  description?: string;
  hsCode?: string;
  countryOfOrigin?: string;
  settingValueId?: string;
  finishValueId?: string;
  claspValueId?: string;
  backingValueId?: string;
  hallmarkValueId?: string;
  /** CR-45 — from the purities, colours and carats columns. */
  offeredValueIds?: string[];
}

export interface ImportVariant {
  productCode: string;
  code?: string;
  purity: string | null;
  metalColour: string | null;
  caratCode: string | null;
  netMetalWeightG?: string;
  grossWeightG?: string;
  totalCaratWeight?: string;
  /** A decimal string, straight from the cell. Never a number (NF-04). */
  listPrice: string;
  attributes: Record<string, unknown>;
}

export interface Submission {
  products: ImportProduct[];
  variants: ImportVariant[];
  /** Rows refused before anything is sent. They are left out of the submission (BU-05). */
  errors: RowError[];
  /** The server's refusal `ref` → the row it came from, so its report can name the row too. */
  rowOf: Map<string, { sheet: SheetKind; row: number }>;
}

/** Which sheet a set of rows is, from its header — names differ between CSV and workbook. */
export function sheetKind(header: readonly string[]): SheetKind | null {
  const h = new Set(header.map((c) => c.trim()));
  if (h.has('product_name')) return 'products';
  if (h.has('list_price')) return 'variants';
  return null;
}

/** RFC 4180, enough for the template: quoted fields, doubled quotes, CRLF or LF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; } else if (c === '"') quoted = false; else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') { row.push(field); field = ''; } else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; } else if (c !== '\r') field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}

/** CSV cell, quoted only when it has to be. */
const cell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
export const toCsv = (rows: readonly (readonly string[])[]) => `${rows.map((r) => r.map(cell).join(',')).join('\n')}\n`;

const DECIMAL = /^\d{1,15}(\.\d{1,4})?$/;

/** The server's ref for a variant it refuses before deriving a code — `catalog-bulk.service.ts`. */
export const variantRef = (v: Pick<ImportVariant, 'code' | 'productCode' | 'purity' | 'metalColour' | 'caratCode'>) =>
  v.code ?? `${v.productCode}/${v.purity}/${v.metalColour}/${v.caratCode}`;

/**
 * Build the submission. Every refusal here is one the server would also make, made
 * earlier so it can name the column — the server names a code, never a column.
 */
export function buildSubmission(sheets: readonly RawSheet[], groups: readonly AttributeGroup[]): Submission {
  const errors: RowError[] = [];
  const rowOf = new Map<string, { sheet: SheetKind; row: number }>();
  const products: ImportProduct[] = [];
  const variants: ImportVariant[] = [];

  const codeToId = (key: string) =>
    new Map((groups.find((g) => g.key === key)?.values ?? []).map((v) => [v.code, v.id]));
  const byKey = {
    purity: codeToId('purity'), mcol: codeToId('mcol'), carat: codeToId('carat'),
    setting: codeToId('setting'), finish: codeToId('finish'), clasp: codeToId('clasp'),
    backing: codeToId('backing'), hallmark: codeToId('hallmark'),
  };

  const records = (kind: SheetKind) => sheets
    .filter((s) => sheetKind(s.rows[0] ?? []) === kind)
    .flatMap((s) => {
      const header = (s.rows[0] ?? []).map((c) => c.trim());
      return s.rows.slice(1).map((cells, i) => ({
        row: i + 2,
        get: (col: string) => (cells[header.indexOf(col)] ?? '').trim(),
        blank: cells.every((c) => c.trim() === ''),
      }));
    })
    // The template is pre-formatted to 400 rows; an empty one is not a row anybody wrote.
    .filter((r) => !r.blank);

  for (const r of records('products')) {
    const fail = (column: string, reason: string) => { errors.push({ sheet: 'products', row: r.row, column, reason }); };
    const before = errors.length;
    const codePrefix = r.get('product_code');
    const name = r.get('product_name');
    const metalTypeCode = r.get('metal_type');
    if (!codePrefix) fail('product_code', 'is required');
    if (!name) fail('product_name', 'is required');
    if (!metalTypeCode) fail('metal_type', 'is required');

    // CR-45: the three lists ARE the offered set. Pipe-separated codes, as the template says.
    const offered: string[] = [];
    for (const [column, key] of [['purities', 'purity'], ['colours', 'mcol'], ['carats', 'carat']] as const) {
      for (const code of r.get(column).split('|').map((c) => c.trim()).filter(Boolean)) {
        const id = byKey[key].get(code);
        if (id) offered.push(id); else fail(column, `"${code}" is not offered on this portal`);
      }
    }
    const make: Partial<Record<'settingValueId' | 'finishValueId' | 'claspValueId' | 'backingValueId' | 'hallmarkValueId', string>> = {};
    for (const [column, field] of [
      ['setting', 'settingValueId'], ['finish', 'finishValueId'], ['clasp', 'claspValueId'],
      ['backing', 'backingValueId'], ['hallmark', 'hallmarkValueId'],
    ] as const) {
      const code = r.get(column);
      if (!code) continue;
      const id = byKey[column].get(code);
      if (id) make[field] = id; else fail(column, `"${code}" is not offered on this portal`);
    }
    if (errors.length > before) continue;

    rowOf.set(codePrefix, { sheet: 'products', row: r.row });
    products.push({
      name, codePrefix, metalTypeCode,
      collection: r.get('collection') || null,
      category: r.get('category') || null,
      ...(r.get('description') ? { description: r.get('description') } : {}),
      ...(r.get('hs_code') ? { hsCode: r.get('hs_code') } : {}),
      ...(r.get('country_of_origin') ? { countryOfOrigin: r.get('country_of_origin') } : {}),
      ...make,
      ...(offered.length ? { offeredValueIds: offered } : {}),
    });
  }

  for (const r of records('variants')) {
    const fail = (column: string, reason: string) => { errors.push({ sheet: 'variants', row: r.row, column, reason }); };
    const before = errors.length;
    const productCode = r.get('product_code');
    const listPrice = r.get('list_price');
    if (!productCode) fail('product_code', 'is required');
    if (!listPrice) fail('list_price', 'is required');
    else if (!DECIMAL.test(listPrice)) fail('list_price', `"${listPrice}" is not an amount — digits, and up to four decimals`);
    for (const column of ['net_metal_weight_g', 'gross_weight_g', 'total_carat_weight']) {
      const v = r.get(column);
      if (v && !DECIMAL.test(v)) fail(column, `"${v}" is not a number`);
    }
    if (errors.length > before) continue;

    const v: ImportVariant = {
      productCode,
      ...(r.get('variant_code') ? { code: r.get('variant_code') } : {}),
      purity: r.get('purity') || null,
      metalColour: r.get('metal_colour') || null,
      caratCode: r.get('centre_stone_carat') || null,
      ...(r.get('net_metal_weight_g') ? { netMetalWeightG: r.get('net_metal_weight_g') } : {}),
      ...(r.get('gross_weight_g') ? { grossWeightG: r.get('gross_weight_g') } : {}),
      ...(r.get('total_carat_weight') ? { totalCaratWeight: r.get('total_carat_weight') } : {}),
      listPrice,
      attributes: {},
    };
    rowOf.set(variantRef(v), { sheet: 'variants', row: r.row });
    variants.push(v);
  }

  return { products, variants, errors, rowOf };
}

/**
 * BU-04 — the server's refusals, with the row put back on where it can be.
 *
 * A refusal raised after the server derived a variant's code carries that code,
 * which the browser cannot rebuild ("Never derived in the browser"), so it comes
 * back without a row and keeps the code instead.
 */
export function placeRefusals(
  refused: readonly BulkRefusal[],
  rowOf: ReadonlyMap<string, { sheet: SheetKind; row: number }>,
): Array<BulkRefusal & { sheet: SheetKind | null; row: number | null }> {
  return refused.map((r) => {
    const at = rowOf.get(r.ref);
    return { ...r, sheet: at?.sheet ?? (r.kind === 'product' ? 'products' : 'variants'), row: at?.row ?? null };
  });
}

/** BU-05 — the correction file: every row that did not go in, and why. Derived, never stored. */
export function errorFileRows(
  parseErrors: readonly RowError[],
  refusals: ReadonlyArray<BulkRefusal & { sheet: SheetKind | null; row: number | null }>,
): string[][] {
  return [
    ['sheet', 'row', 'column', 'reference', 'reason'],
    ...parseErrors.map((e) => [e.sheet, String(e.row), e.column, '', e.reason]),
    ...refusals.map((r) => [r.sheet ?? '', r.row === null ? '' : String(r.row), '', r.ref, r.reason]),
  ];
}
